import { useSyncExternalStore } from "react";
import { flushSync } from "react-dom";
import { storage } from "./utils";

/**
 * UI preferences that must apply before first paint (theme, language, density) live in
 * localStorage, are stamped on <html> by the inline script in index.html, and are kept
 * in sync here. They are per-device conveniences, never account data.
 */
export type ThemePref = "system" | "light" | "dark";
export type Lang = "ar" | "en";
export type Density = "comfortable" | "compact";

type Prefs = { theme: ThemePref; lang: Lang; density: Density; navCollapsed: boolean; accountId: string | null; floatingAsk: boolean; collapsedGroups: string[] };

const read = (): Prefs => ({
  theme: (storage.get("malfak.theme") as ThemePref) || "system",
  lang: (storage.get("malfak.lang") as Lang) || "ar",
  density: (storage.get("malfak.density") as Density) || "comfortable",
  navCollapsed: storage.get("malfak.nav") === "collapsed",
  accountId: storage.get("malfak.account"),
  // ALP-84: the assistant opens from the topbar "Ask" button; the floating button is an opt-in preference
  floatingAsk: storage.get("malfak.floatingAsk") === "1",
  collapsedGroups: storage.json<string[]>("malfak.nav.groups", []),
});

let state = read();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

const media = typeof window !== "undefined" ? window.matchMedia?.("(prefers-color-scheme: dark)") : undefined;

export function resolvedTheme(pref: ThemePref = state.theme): "light" | "dark" {
  if (pref === "system") return media?.matches ? "dark" : "light";
  return pref;
}

function apply() {
  const root = document.documentElement;
  root.setAttribute("data-theme", resolvedTheme());
  root.lang = state.lang;
  root.dir = state.lang === "ar" ? "rtl" : "ltr";
  root.setAttribute("data-density", state.density);
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute("content", resolvedTheme() === "dark" ? "#0b0f17" : "#ffffff");
}

media?.addEventListener?.("change", () => {
  if (state.theme === "system") {
    apply();
    emit();
  }
});

// last pointer position: the theme switch reveals from where the user clicked
let lastPointer = { x: 0, y: 0 };
if (typeof window !== "undefined") window.addEventListener("pointerdown", (e) => (lastPointer = { x: e.clientX, y: e.clientY }), { capture: true, passive: true });

/**
 * Smooth theme and language switches with the View Transitions API: the new theme
 * spreads in a circle from the pointer, a language/direction change cross-fades.
 * Falls back to an instant switch (no API, reduced motion, tests).
 */
function transition(kind: "theme" | "lang", update: () => void) {
  const doc = typeof document !== "undefined" ? (document as Document & { startViewTransition?: (cb: () => void) => { finished: Promise<void> } }) : null;
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (!doc?.startViewTransition || reduce) return update();
  const root = doc.documentElement;
  const x = lastPointer.x || innerWidth / 2;
  const y = lastPointer.y || 0;
  root.style.setProperty("--vt-x", `${x}px`);
  root.style.setProperty("--vt-y", `${y}px`);
  root.style.setProperty("--vt-r", `${Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y))}px`);
  root.classList.add(`vt-${kind}`);
  const vt = doc.startViewTransition(() => flushSync(update));
  void vt.finished.finally(() => root.classList.remove(`vt-${kind}`));
}

export const prefs = {
  get: () => state,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  setTheme(theme: ThemePref) {
    state = { ...state, theme };
    storage.set("malfak.theme", theme);
    transition("theme", () => {
      apply();
      emit();
    });
  },
  setLang(lang: Lang) {
    state = { ...state, lang };
    storage.set("malfak.lang", lang);
    transition("lang", () => {
      apply();
      emit();
    });
  },
  setDensity(density: Density) {
    state = { ...state, density };
    storage.set("malfak.density", density);
    apply();
    emit();
  },
  setNavCollapsed(navCollapsed: boolean) {
    state = { ...state, navCollapsed };
    storage.set("malfak.nav", navCollapsed ? "collapsed" : "open");
    emit();
  },
  setFloatingAsk(floatingAsk: boolean) {
    state = { ...state, floatingAsk };
    storage.set("malfak.floatingAsk", floatingAsk ? "1" : "0");
    emit();
  },
  /** Sidebar groups the user folded (per device). */
  toggleGroup(group: string) {
    const collapsedGroups = state.collapsedGroups.includes(group) ? state.collapsedGroups.filter((g) => g !== group) : [...state.collapsedGroups, group];
    state = { ...state, collapsedGroups };
    storage.set("malfak.nav.groups", JSON.stringify(collapsedGroups));
    emit();
  },
  /** The channel account the dashboard/analytics focus on (null = all accounts). */
  setAccountId(accountId: string | null) {
    state = { ...state, accountId };
    storage.set("malfak.account", accountId);
    emit();
  },
  apply,
};

export function usePrefs(): Prefs & { resolvedTheme: "light" | "dark" } {
  const s = useSyncExternalStore(prefs.subscribe, prefs.get, prefs.get);
  return { ...s, resolvedTheme: resolvedTheme(s.theme) };
}
